/**
 * A workflow's versions, newest as the server lists them, each openable in
 * the editor (ADR-0009). Split out of the editor in Wave 15F.
 */

import { Badge, type BadgeTone, Button } from '@fiveox/ui'
import { History } from 'lucide-react'

import { RecordSection } from '../../components/page'
import type { VersionSummary } from '../../lib/api/services'

/** Published is the one cases follow; a draft is being worked on; an archived
 *  one is kept for the cases pinned to it. */
export const VERSION_TONE: Record<VersionSummary['status'], BadgeTone> = {
  published: 'success',
  draft: 'info',
  archived: 'neutral',
}

export function Versions({
  versions,
  openId,
  onOpen,
}: {
  versions: VersionSummary[]
  openId: string
  onOpen: (id: string) => void
}) {
  return (
    <RecordSection
      id="workflow-versions"
      icon={<History size={18} aria-hidden />}
      title="Versions"
      description="A case keeps the version it started under. Open one to read it."
    >
      <ul className="divide-border divide-y">
        {versions.map((v) => (
          <li
            key={v.id}
            data-version={v.version}
            className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm first:pt-0 last:pb-0"
          >
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-foreground font-medium">Version {v.version}</span>
              <Badge tone={VERSION_TONE[v.status]} size="sm">
                {v.status}
              </Badge>
              {v.in_use && (
                <Badge tone="neutral" size="sm">
                  in use
                </Badge>
              )}
              {v.notes && <span className="text-muted-foreground">— {v.notes}</span>}
            </span>
            {v.id === openId ? (
              <span className="text-muted-foreground text-xs">Open now</span>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => onOpen(v.id)}>
                Open
              </Button>
            )}
          </li>
        ))}
      </ul>
    </RecordSection>
  )
}
